import classnames from 'classnames'
import { Component } from 'preact'
import Tooltip from '@insided/deprecated-control/ui-components/Tooltip'
import Icon from '@insided/deprecated-preact-components/assets'

export interface TextFieldProps {
  name: string
  ariaLabel: string
  id: string
  label?: string
  subtext?: string
  isControl?: boolean
  isInline?: boolean
  disabled?: boolean
  enableFocusToInput?: boolean
  updateInputValue?: string
  type?: 'text' | 'number' | 'url'
  placeholder?: string
  defaultValue?: string | number
  direction?: 'vertical' | 'horizontal'
  size?: 'S' | 'M' | 'L' | 'fullWidth'
  onChange?: (value: string) => void
  onKeyUp?: (value: string) => void
  onBlur?: (e: FocusEvent) => void
  className?: string
  contextText?: string
  tooltipMessage?: string
  tooltipHeading?: string
}

interface State {
  inputValue: string | number
}

class TextField extends Component<TextFieldProps, State> {
  input: HTMLInputElement | null = null

  constructor(props: TextFieldProps) {
    super(props)

    this.state = {
      inputValue: props.defaultValue ?? ''
    }

    this.handleOnChange = this.handleOnChange.bind(this)
    this.handleOnKeyUp = this.handleOnKeyUp.bind(this)
  }

  handleOnChange(value: string) {
    this.setState({
      inputValue: value
    })

    if (this.props.onChange && typeof this.props.onChange === 'function') {
      this.props.onChange(value)
    }
  }

  handleOnKeyUp(value: string) {
    this.setState({
      inputValue: value
    })
    if (this.props.onKeyUp && typeof this.props.onKeyUp === 'function') {
      this.props.onKeyUp(value)
    }
  }

  componentDidUpdate(previousProps: Readonly<TextFieldProps>) {
    if (
      previousProps.enableFocusToInput !== this.props.enableFocusToInput &&
      this.props.enableFocusToInput &&
      this.input
    ) {
      this.input.focus()
      setTimeout(() => {
        if (this.input) {
          this.input.focus()
        }
      }, 100)
    }
    if (
      previousProps.updateInputValue !== this.props.updateInputValue &&
      typeof this.props.updateInputValue === 'string' &&
      this.input
    ) {
      this.input.value = this.props.updateInputValue
      this.setState({ inputValue: this.props.updateInputValue })
      this.handleOnChange(this.props.updateInputValue)
      this.handleOnKeyUp(this.props.updateInputValue)
    }
  }

  render() {
    const { inputValue } = this.state
    const { name, ariaLabel, id, label, subtext, placeholder = '', className, onBlur, size, isInline, disabled = false, direction, isControl, type = 'text', contextText = '', tooltipMessage = '', tooltipHeading = '' } = this.props

    return (
      <div
        className={classnames(
          'a-text-field',
          `a-text-field--size-${size ?? 'fullWidth'}`,
          `a-text-field--direction-${direction ?? 'vertical'}`,
          {
            'a-text-field--type-inline': isInline,
            'a-text-field--app-control': isControl,
            'a-text-field--app-destination': !isControl
          }
        )}
      >
        {Boolean(label) && (
          <label className='a-text-field--label' for={id}>
            {label}
            {Boolean(tooltipMessage) && (
              <Tooltip
                trigger={<Icon icon='info' width={16} height={16} customClass='icon-info' />}
                tooltipMessage={(
                  <div>
                    {Boolean(tooltipHeading) && (
                      <div>
                        <b>{tooltipHeading}</b>
                      </div>
                    )}
                    <div>{tooltipMessage}</div>
                  </div>
                )}
                position='center'
              />
            )}
          </label>
        )}
        {Boolean(subtext) && <p className='a-text-field--subtext'>{subtext}</p>}
        <div className='a-text-field--input-container'>
          {Boolean(contextText) && (
            <div className='a-text-field--context-text'>
              {contextText}
            </div>
          )}
          <input
            ref={(input: HTMLInputElement) => {
              this.input = input
            }}
            placeholder={placeholder}
            className={`a-text-field--input ${className}`}
            id={id}
            type={type}
            name={name}
            aria-label={ariaLabel}
            value={inputValue}
            onChange={(event) => this.handleOnChange((event.target as HTMLInputElement).value)}
            onKeyUp={(event) => this.handleOnKeyUp((event.target as HTMLInputElement).value)}
            onBlur={onBlur}
            disabled={disabled}
          />
        </div>
      </div>
    )
  }
}

export default TextField
